import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { getAccountantHomePath, isAccountantUser } from '../utils/limitedUserAccess';
import api from '../services/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const hasLoggedApi = useRef(false);

  useEffect(() => {
    // Obtener la URL base del API solo una vez (solo para logs en consola)
    if (!hasLoggedApi.current) {
      const baseURL = api.defaults.baseURL;
      console.log('🔗 [Login] Conectando a:', baseURL);
      hasLoggedApi.current = true;
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('🔵 [Login] Botón Ingresar clickeado');
    console.log('📧 [Login] Email:', email);
    console.log('🔒 [Login] Intentando iniciar sesión...');

    try {
      const loginUrl = `${api.defaults.baseURL}/login`;
      console.log('🌐 [Login] Enviando petición POST a:', loginUrl);
      
      const response = await authService.login(email, password);
      
      console.log('✅ [Login] Login exitoso');
      console.log('👤 [Login] Usuario:', response.user);
      console.log('🔑 [Login] Token recibido');
      
      const loggedUser = response.user || authService.getCurrentUser();
      navigate(isAccountantUser(loggedUser) ? getAccountantHomePath() : '/dashboard');
    } catch (err) {
      console.error('❌ [Login] Error en login:', err);
      console.error('📊 [Login] Respuesta del servidor:', err.response?.data);
      console.error('🔢 [Login] Status:', err.response?.status);
      
      setError(
        err.response?.data?.message || 
        err.response?.data?.errors?.email?.[0] || 
        'Error al iniciar sesión. Por favor, verifica tus credenciales.'
      );
    } finally {
      setLoading(false);
      console.log('🏁 [Login] Proceso de login finalizado');
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md px-5">
        {/* Título del Sistema */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-semibold text-teal-600 mb-2">
            Contifine Logistic
          </h1>
        </div>

        {/* Tarjeta de Login */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-medium text-gray-700 mb-6 text-center">
            Iniciar Sesión
          </h2>
          
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-md mb-4 text-sm border border-red-200">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Campo Correo Electrónico */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Correo Electrónico
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="usuario@ejemplo.com"
                  className="w-full pl-10 pr-3 py-2.5 border border-teal-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            </div>
            
            {/* Campo Contraseña */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-3 py-2.5 border border-teal-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
            </div>
            
            {/* Checkbox Mantenerme Conectado */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
                className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded cursor-pointer disabled:opacity-50"
              />
              <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-600 cursor-pointer">
                Mantenerme conectado
              </label>
            </div>
            
            {/* Botón Ingresar */}
            <button 
              type="submit" 
              className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-md text-base transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Ingresando...' : 'Ingresar'}
            </button>
          </form>
          
          {/* Link Olvidé mi Contraseña */}
          <div className="mt-6 text-center">
            <a href="#" className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
              ¿Olvidaste tu contraseña?
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
